import { Fragment, useEffect } from "react";
import styled from "styled-components";
import { Controller, useFormContext } from "react-hook-form";
import isToday from 'date-fns/isToday';
import format from "date-fns/format";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import useQuery from "hooks/useQueryCustom";
import { getCountryList } from "services";
import { useSelector } from "react-redux";
import { currentUserSelector } from "redux/selectors";
import EnumDropdown from "components/Dropdowns/EnumDropdown";
import UniversalInput from "components/Input/UniversalInput";
import DatePicker from "components/DatePickers/DatePicker";
import SelectDropdown from "components/Dropdowns/SelectDropdown";
import CircularProgress from "@mui/material/CircularProgress";
import { PatternFormat } from "react-number-format";

import { StepTitle, StepDesc } from "./Welcome";
import { FieldItem } from './PersonalDetails';

interface IEmploymentEligibility {
    updatedAt: Date
};

export default function EmploymentEligibility({ updatedAt }: IEmploymentEligibility) {
    const currentUser = useSelector(currentUserSelector);
    const { control, register, setValue, unregister, watch, formState: { errors } } = useFormContext();

    const { data: { list: eligibilityTypes = [] } = [] } = useQuery<any>(["enum_eligibility_types"], {
        endpoint: "/enum/list?&model_name=Enum::EligibilityType",
        options: { method: "get" },
    }, { refetchOnWindowFocus: false });

    const { data: { list: authorizationDocs = [] } = [] } = useQuery<any>(["enum_authorization_docs"], {
        endpoint: "/enum/list?&model_name=Enum::AuthorizationDocument",
        options: { method: "get" },
    }, { refetchOnWindowFocus: false });

    const { data: eligibilityData, isLoading } = useQuery<any>(["get_eligibility_step"], {
        endpoint: `eligibility?employee_id=${currentUser?.employee.id}`,
        options: { method: "get" },
    }, { refetchOnWindowFocus: false });

    useEffect(() => {
        if (eligibilityData) {
            setValue('eligibility_type', eligibilityData.eligibility_type?.id);
            setValue('uscis_number', eligibilityData.uscis_number);
            setValue('expiration_date', eligibilityData?.expiration_date ? utcToZonedTime(new Date(eligibilityData.expiration_date), 'UTC') : '');
            setValue('authorization_document', eligibilityData.authorization_document?.id);
            setValue('i94_number', eligibilityData.i94_number);
            setValue('passport_number', eligibilityData.passport_number);
            setValue('country', eligibilityData.country);
        };
    }, [eligibilityData, setValue]);

    const watchIam = watch('eligibility_type');
    const watchAuthDoc = watch('authorization_document');

    const getEligibilityTypeName = (id: number) => {
        return eligibilityTypes.find((e: any) => e.id === id)?.id_name;
    };

    const getAuthorizationDocName = (id: number) => {
        return authorizationDocs.find((e: any) => e.id === id)?.id_name;
    };

    const renderRelevantDesc = (id: number) => {
        const item = getEligibilityTypeName(id);
        if (item === 'citizen') return "A citizen is someone who was born in the United States or is a naturalized citizen living in the United States.";
        if (item === 'noncitizen') return "A noncitizen national is someone born in American Samoa, certain former citizens of the former Trust Territory of the Pacific Islands, and certain children of noncitizen nationals born abroad.";
        if (item === 'resident') return "A lawful permanent resident is someone who is not a US citizen and who resides under legally recognized and lawfully recorded permanent residence as an immigrant.";
        if (item === 'authorized') return "An alien authorized to work includes anyone who is authorized to work in the United States but is not a US citizen, US national or lawful permanent resident.";
    };

    const uscisNumberField = () => {
        return (
            <FieldItem label="USCIS/Alien Registration Number" required large>
                <UniversalInput
                    inputProps={{ maxLength: 10, minLength: 7 }}
                    {...register('uscis_number', {
                        required: "USCIS/Alien Registration Number is required",
                        pattern: {
                            value: /([A]{1})+([0-9]{7}|[0-9]{8}|[0-9]{9})*$/,
                            message: 'Document number is invalid'
                        },
                        maxLength: {
                            value: 10,
                            message: 'Document number is invalid'
                        },
                        minLength: {
                            value: 7,
                            message: 'Document number is invalid'
                        }
                    })}
                    errorText={errors.uscis_number ? errors.uscis_number.message : ''}
                />
                <FieldDescription>
                    Fill in a 7-9 digit USCIS Number or Alien Registration Number (include the "A")
                </FieldDescription>
            </FieldItem>
        )
    };

    const eligibilityTypeOnChange = (e: any) => {
        let id: number = e.target.value;
        if (getEligibilityTypeName(id) === 'citizen' || getEligibilityTypeName(id) === 'noncitizen') {
            unregister(['uscis_number', 'expiration_date', 'authorization_document', 'i94_number', 'passport_number', 'country']);
        };
    };

    if (isLoading) return <LoadingScreenContainer><CircularProgress thickness={4} /></LoadingScreenContainer>;

    return (
        <ContentContainer>
            <StepTitle>EMPLOYMENT ELIGIBILITY</StepTitle>
            <StepDesc>
                The federal government requires verification of your eligibility to work in the US.<br />
                This info will be used to fill out Form I-9, which proves that you’re legally allowed to work in the US.
                <span>Last Saved {isToday(new Date(updatedAt)) ? 'Today' : format(new Date(updatedAt), 'PP')} at {format(new Date(updatedAt), 'p')}</span>
            </StepDesc>
            <FieldsContainer>
                <FieldItem label="I am" required large>
                    <Controller
                        name="eligibility_type"
                        rules={{ required: 'Employment Eligibility is required' }}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <EnumDropdown
                                placeholder='Select Employment Eligibility'
                                onChange={(e) => { onChange(e); eligibilityTypeOnChange(e); }}
                                value={value}
                                options={eligibilityTypes}
                                errorText={errors.eligibility_type ? errors.eligibility_type.message : '' as any}
                            />
                        )}
                    />
                    {watchIam ? <FieldDescription>{renderRelevantDesc(watchIam)}</FieldDescription> : null}
                </FieldItem>
                {getEligibilityTypeName(watchIam) === 'resident' && uscisNumberField()}
                {getEligibilityTypeName(watchIam) === 'authorized' && <Fragment>
                    <FieldItem label="Authorized to work until" required>
                        <Controller
                            name="expiration_date"
                            control={control}
                            rules={{ validate: value => value === null ? 'Please enter a valid date' : value !== '' || 'Authorized to work until is required' }}
                            render={({ field: { onChange, value } }: any) => (
                                <DatePicker
                                    selected={value}
                                    onChange={onChange}
                                    errorText={errors.expiration_date ? errors.expiration_date.message : ''}
                                />
                            )}
                        />
                    </FieldItem>
                    <FieldItem label="Authorization Documentation" required large>
                        <Controller
                            name="authorization_document"
                            rules={{ required: 'Authorization Documentation is required' }}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <EnumDropdown
                                    placeholder='Select Authorization Documentation'
                                    onChange={onChange}
                                    value={value}
                                    options={authorizationDocs}
                                    errorText={errors.authorization_document ? errors.authorization_document.message : '' as any}
                                />
                            )}
                        />
                    </FieldItem>
                    {getAuthorizationDocName(watchAuthDoc) === 'uscis_number' && uscisNumberField()}
                    {getAuthorizationDocName(watchAuthDoc) === 'i94' &&
                        <FieldItem label="Form I-94 Admission Number" required large>
                            <Controller
                                name="i94_number"
                                control={control}
                                rules={{
                                    required: 'Form I-94 Admission Number is required',
                                    maxLength: {
                                        value: 11,
                                        message: "Form I-94 Admission Number is invalid"
                                    },
                                    minLength: {
                                        value: 11,
                                        message: "Form I-94 Admission Number is invalid"
                                    }
                                }}
                                render={({ field: { onChange, value, ref } }) => (
                                    <StyledNumberFormatInput
                                        type='tel'
                                        format="#########-##"
                                        mask={"_"}
                                        valueIsNumericString
                                        value={value}
                                        onValueChange={(values: any) => onChange(values.value)}
                                        $inputError={!!errors.i94_number}
                                        getInputRef={ref}
                                    />
                                )}
                            />
                            <FieldDescription>Fill in your 11 digit I-94 admission number</FieldDescription>
                            <SSNError>{errors.i94_number ? errors.i94_number.message : ''}</SSNError>
                        </FieldItem>}
                    {getAuthorizationDocName(watchAuthDoc) === 'passport' &&
                        <Fragment>
                            <FieldItem label="Foreign Passport Number" required large>
                                <UniversalInput
                                    inputProps={{ maxLength: 50 }}
                                    {...register('passport_number', {
                                        required: "Foreign Passport Number is required",
                                        maxLength: {
                                            value: 50,
                                            message: "Maximum length is 50 symbol"
                                        }
                                    })}
                                    errorText={errors.passport_number ? errors.passport_number.message : ''}
                                />
                            </FieldItem>
                            <FieldItem label="Country of Issuance" required large>
                                <Controller
                                    name="country"
                                    control={control}
                                    rules={{ required: 'Country of Issuance is required' }}
                                    render={({ field: { onChange, value } }) => (
                                        <SelectDropdown
                                            inputPlaceholder='Select Country of Issuance'
                                            onChange={(_event: React.SyntheticEvent<Element, Event>, newValue: any) => {
                                                onChange(newValue)
                                            }}
                                            value={value}
                                            loadRemoteData={() => getCountryList(300, 1)}
                                            errorText={errors.country ? errors.country.message : ''}
                                        />
                                    )}
                                />
                            </FieldItem>
                        </Fragment>}
                </Fragment>}
            </FieldsContainer>
        </ContentContainer>
    )
};

const ContentContainer = styled.div`
    flex: 1;
    padding-top: 60px;
    overflow-y: auto;
`;

const FieldsContainer = styled.div`
    margin-top: 20px;
    margin-right: 60px;
`;

const FieldDescription = styled.p`
    font-size: 11px;
    color: #636D73;
    margin-top: 5px;
`;

const StyledNumberFormatInput = styled(PatternFormat) <{ $inputError: boolean }>`
    width: 100%;
    border-radius: 4px;
    border: ${({ $inputError }) => $inputError ? '1px solid var(--red)' : '1px solid #D6D6D6'};
    padding: 10px 13px;

    &:focus {
      border-color:  ${({ $inputError }) => $inputError ? 'var(--red)' : '#99CC33'};
    }
`;

const SSNError = styled.span`
    color: var(--red);
    margin-top: 6px;
    font-size: 12px;
    display: inline-block;
`;

const LoadingScreenContainer = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   flex: 1;
`;